import { Link } from 'react-router-dom';

const pipelineItems = [
  { label: 'Lead Captured', value: '142', change: '+12 today', color: 'bg-amber-500', width: 'w-full' },
  { label: 'Qualified', value: '98', change: '+8 today', color: 'bg-blue-500', width: 'w-10/12' },
  { label: 'Booked', value: '74', change: '+6 today', color: 'bg-indigo-500', width: 'w-8/12' },
  { label: 'Job Completed', value: '61', change: '+5 today', color: 'bg-emerald-500', width: 'w-6/12' },
];

const liveAlerts = [
  { icon: 'ri-phone-fill', text: 'Missed call captured — HVAC lead', time: '2m ago', dot: 'bg-amber-400' },
  { icon: 'ri-calendar-check-fill', text: 'Job booked — Plumbing estimate', time: '7m ago', dot: 'bg-emerald-400' },
  { icon: 'ri-mail-fill', text: 'Follow-up sent — Roofing proposal', time: '14m ago', dot: 'bg-blue-400' },
];

export default function Hero() {
  return (
    <section className="relative min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center pt-24 pb-0 overflow-hidden">
      {/* Subtle radial glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-amber-500/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-40 left-1/4 w-[400px] h-[400px] bg-blue-600/6 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center w-full">
        <div className="inline-flex items-center gap-2 bg-white/8 border border-white/10 text-white/70 text-xs font-medium px-4 py-1.5 rounded-full mb-8 backdrop-blur-sm">
          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
          AI Automation for Home Services &amp; Solar
        </div>

        <h1 className="text-6xl lg:text-8xl font-extrabold text-white leading-none tracking-tighter mb-6">
          Turn Every Lead Into
          <br />
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage: 'linear-gradient(90deg, #F59E0B 0%, #FBBF24 50%, #FCD34D 100%)',
            }}
          >
            a Booked Job.
          </span>
        </h1>

        <p className="text-white/55 text-xl lg:text-2xl font-light leading-relaxed max-w-2xl mx-auto mb-10 tracking-tight">
          Omega Solutions AI automates your calls, scheduling, and follow-ups—
          so you never miss revenue again.
        </p>

        <div className="flex flex-wrap gap-3 justify-center mb-20">
          <Link
            to="/services"
            className="whitespace-nowrap bg-white text-[#0A0A0A] font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/90 transition-all cursor-pointer"
          >
            Get Your Automation Audit
          </Link>
          <a
            href="#how-it-works"
            className="whitespace-nowrap bg-white/10 text-white/85 border border-white/15 font-medium text-sm px-7 py-3.5 rounded-full hover:bg-white/15 transition-all cursor-pointer backdrop-blur-sm"
          >
            See How It Works
          </a>
        </div>

        {/* Floating dashboard visual */}
        <div className="relative mx-auto max-w-4xl">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-3/4 h-16 bg-amber-500/20 blur-2xl rounded-full" />
          <div className="bg-[#141414] border border-white/10 rounded-3xl overflow-hidden">
            {/* Dashboard header bar */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/6 bg-white/3">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
              </div>
              <span className="text-white/30 text-xs font-medium tracking-wider">OMEGA PIPELINE — LIVE</span>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                <span className="text-green-400 text-xs">Live</span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* Pipeline column */}
              <div className="lg:col-span-2 p-6 border-r border-white/6">
                <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-5">Pipeline Overview</p>
                <div className="space-y-4">
                  {pipelineItems.map((item) => (
                    <div key={item.label}>
                      <div className="flex justify-between mb-1.5">
                        <span className="text-white/70 text-sm font-medium">{item.label}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-white/30 text-xs">{item.change}</span>
                          <span className="text-white font-bold text-sm">{item.value}</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-white/6 rounded-full overflow-hidden">
                        <div className={`h-full ${item.color} ${item.width} rounded-full transition-all`} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mini stats row */}
                <div className="grid grid-cols-3 gap-3 mt-6 pt-5 border-t border-white/6">
                  {[
                    { label: 'Booked Today', val: '+6', color: 'text-emerald-400' },
                    { label: 'Avg Response', val: '1.8m', color: 'text-amber-400' },
                    { label: 'Close Rate', val: '67%', color: 'text-blue-400' },
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <div className={`text-xl font-extrabold ${s.color} mb-0.5`}>{s.val}</div>
                      <div className="text-white/35 text-xs">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Live activity column */}
              <div className="p-6">
                <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-5">Live Activity</p>
                <div className="space-y-4">
                  {liveAlerts.map((alert) => (
                    <div key={alert.text} className="flex items-start gap-3">
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg bg-white/6 flex-shrink-0 mt-0.5`}>
                        <i className={`${alert.icon} text-white/60 text-xs`} />
                      </div>
                      <div>
                        <p className="text-white/70 text-xs leading-snug">{alert.text}</p>
                        <p className="text-white/25 text-xs mt-0.5">{alert.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-white/6">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                    <span className="text-emerald-400/80 text-xs">All systems running 24/7</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fade-to-white transition at bottom */}
      <div className="w-full h-32 bg-gradient-to-b from-transparent to-white mt-0 relative z-10" />
    </section>
  );
}
